import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Divider,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Subscriptions as SubscriptionsIcon,
  Money as MoneyIcon,
  People as PeopleIcon,
  Add as AddIcon,
  Payments as PaymentsIcon,
  Analytics as AnalyticsIcon,
  CardMembership as CardMembershipIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  History as HistoryIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useUser } from '../../../contexts/UserContext';
import { collection, query, where, getDocs, doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { formatCurrency } from '../../../utils/formatting';

// Import components that we'll create later
import SubscriptionTierForm from './components/SubscriptionTierForm';
import SubscriptionTierList from './components/SubscriptionTierList';
import SubscribersList from './components/SubscribersList';
import AnalyticsTab from './components/AnalyticsTab';
import PublicMembershipLink from './components/PublicMembershipLink';

// Interface for subscription tier
export interface SubscriptionTier {
   id: string;
   name: string;
   monthlyPrice: number;
   annualPrice: number;
   description: string;
   price?: number;
   type?: 'monthly' | 'annual';
   benefits: {
      earlyAccess: boolean;
      discounts: boolean;
      privateEvents: boolean;
      exclusiveContent: boolean;
      prioritySupport: boolean;
      customBenefit1?: {
         [key: string]: boolean;
      };
      customBenefit2?: {
         [key: string]: boolean;
      };
   };
   memberLimit: number;
   unlimitedMembers: boolean;
   active: boolean;
   createdAt: Timestamp;
   updatedAt: Timestamp;
   isPrivateForMembers: boolean;
}

// Interface for organization subscription settings
interface SubscriptionSettings {
  enabled: boolean;
  activatedDate?: Timestamp;
  lastBillingDate?: Timestamp;
  nextBillingDate?: Timestamp;
  platformFee: number; // Percentage
  totalRevenue: number;
  platformFeeCollected: number;
}

// Interface for subscriber
export interface Subscriber {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPhotoURL?: string;
  tierId: string;
  tierName: string;
  subscribedDate: Timestamp;
  renewalDate: Timestamp;
  paymentStatus: 'active' | 'past_due' | 'canceled' | 'trial';
  isAnnual: boolean;
  amount: number;
}

// Main component
const OrganizationSubscriptions: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeOrganization, isPersonalAccount, isOrgAdmin } = useOrganization();
  const { currentUser } = useUser();
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Subscription data states
  const [subscriptionSettings, setSubscriptionSettings] = useState<SubscriptionSettings>({
    enabled: false,
    platformFee: 5, // 5% platform fee
    totalRevenue: 0,
    platformFeeCollected: 0
  });
  
  // Metrics
  const [metrics, setMetrics] = useState({
    totalSubscribers: 0,
    activeSubscribers: 0,
    monthlyRecurringRevenue: 0,
    annualSubscribers: 0,
    monthlySubscribers: 0,
    retentionRate: 0,
    avgSubscriptionValue: 0
  });
  
  // UI state
  const [currentTab, setCurrentTab] = useState(0);
  const [showTierForm, setShowTierForm] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  
  // Check if user is not authorized
  useEffect(() => {
     // if (isPersonalAccount || !isOrgAdmin) {
     //   setError("You don't have permission to access subscription management");
     //   setLoading(false);
     //   return;
     // }

     if (id) {
        fetchSubscriptionData();
     }
  }, [id, isPersonalAccount, isOrgAdmin]);
  
  // Fetch subscription data
  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      
      // Get subscription settings
      const settingsRef = doc(db, `organizations/${id}/settings/subscriptions`);
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        setSubscriptionSettings(settingsDoc.data() as SubscriptionSettings);
      }
      
      // Get metrics if subscription is enabled
      if (settingsDoc.exists() && settingsDoc.data().enabled) {
        // Get subscribers count
        const subscribersRef = collection(db, `organizations/${id}/subscribers`);
        const subscribersSnapshot = await getDocs(subscribersRef);
        const subscribersCount = subscribersSnapshot.size;
        
        // Calculate active subscribers
        const activeSubscribers = subscribersSnapshot.docs.filter(
          doc => doc.data().paymentStatus === 'active'
        ).length;
        
        // Calculate monthly recurring revenue
        let mrr = 0;
        let annualCount = 0;
        let monthlyCount = 0;
        
        subscribersSnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.paymentStatus === 'active') {
            if (data.isAnnual) {
              mrr += data.amount / 12; // Convert annual to monthly
              annualCount++;
            } else {
              mrr += data.amount;
              monthlyCount++;
            }
          }
        });
        
        // Calculate avg subscription value
        const avgValue = subscribersCount > 0 ? 
          mrr / activeSubscribers : 0;
        
        // Set metrics
        setMetrics({
          totalSubscribers: subscribersCount,
          activeSubscribers,
          monthlyRecurringRevenue: mrr,
          annualSubscribers: annualCount,
          monthlySubscribers: monthlyCount,
          retentionRate: 95, // Mock value for now
          avgSubscriptionValue: avgValue
        });
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError('Failed to load subscription data. Please try again.');
      setLoading(false);
    }
  };
  
  // Activate subscription feature
  const handleActivateSubscription = async () => {
    try {
      setIsActivating(true);
      
      // Create or update subscription settings
      const settingsRef = doc(db, `organizations/${id}/settings/subscriptions`);
      const now = Timestamp.now();
      
      const subscriptionData: SubscriptionSettings = {
        enabled: true,
        activatedDate: now,
        lastBillingDate: now,
        nextBillingDate: Timestamp.fromDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)), // 30 days from now
        platformFee: 5,
        totalRevenue: 0,
        platformFeeCollected: 0
      };
      
      await setDoc(settingsRef, subscriptionData);
      
      // Update state
      setSubscriptionSettings(subscriptionData);
      setIsActivating(false);
      
      // Refresh data
      fetchSubscriptionData();
    } catch (err) {
      console.error('Error activating subscription:', err);
      setError('Failed to activate subscription feature. Please try again.');
      setIsActivating(false);
    }
  };
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };
  
  // Main content based on activation status
  const renderContent = () => {
    // If subscription feature is not enabled
    if (!subscriptionSettings.enabled) {
      return (
        <Paper sx={{ p: 4, mt: 3, textAlign: 'center' }}>
          <Box sx={{ maxWidth: 600, mx: 'auto', py: 3 }}>
            <CardMembershipIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" component="h2" gutterBottom>
              Activate Subscription Feature
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Grow your organization with membership subscriptions. Offer exclusive content, early access to events, and special perks to your subscribers.
            </Typography>
            <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                $100/month flat fee
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Plus 5% platform fee on all subscription revenue
              </Typography>
            </Box>
            <Button 
              variant="contained" 
              size="large"
              color="primary"
              onClick={handleActivateSubscription}
              disabled={isActivating}
              sx={{ mt: 4 }}
            >
              {isActivating ? <CircularProgress size={24} color="inherit" /> : 'Activate Subscription Feature'}
            </Button>
          </Box>
        </Paper>
      );
    }
    
    // If subscription feature is enabled, show subscription management interface
    return (
      <>
        {/* Subscription Dashboard */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h2">
              Subscription Dashboard
            </Typography>
            <Chip 
              label="Active" 
              color="success" 
              icon={<CardMembershipIcon />} 
              variant="outlined"
            />
          </Box>
          
          <Grid container spacing={3}>
            {/* Total Subscribers */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total Subscribers
                  </Typography>
                  <Typography variant="h4" component="div">
                    {metrics.totalSubscribers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {metrics.activeSubscribers} active
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Monthly Recurring Revenue */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Monthly Revenue
                  </Typography>
                  <Typography variant="h4" component="div">
                    {formatCurrency(metrics.monthlyRecurringRevenue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Platform fee: {formatCurrency(metrics.monthlyRecurringRevenue * (subscriptionSettings.platformFee / 100))}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Subscriber Distribution */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Subscription Type
                  </Typography>
                  <Typography variant="h4" component="div">
                    {metrics.annualSubscribers + metrics.monthlySubscribers > 0 ? 
                      `${Math.round((metrics.annualSubscribers / (metrics.annualSubscribers + metrics.monthlySubscribers)) * 100)}%` : 
                      '0%'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Annual subscribers
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Average Subscription Value */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Avg. Subscription
                  </Typography>
                  <Typography variant="h4" component="div">
                    {formatCurrency(metrics.avgSubscriptionValue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Per subscriber/month
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      
        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab icon={<CardMembershipIcon />} label="Subscription Tiers" />
            <Tab icon={<PeopleIcon />} label="Subscribers" />
            <Tab icon={<AnalyticsIcon />} label="Analytics" />
            <Tab icon={<PaymentsIcon />} label="Payments" />
            <Tab icon={<SettingsIcon />} label="Settings" />
            <Tab icon={<LinkIcon />} label="Public Link" />
          </Tabs>
        </Paper>
        
        {/* Tab content */}
        <TabPanel value={currentTab} index={0}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" component="h3">
              Subscription Tiers
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setShowTierForm(true)}
            >
              Create New Tier
            </Button>
          </Box>
          
          {showTierForm ? (
            <SubscriptionTierForm 
              organizationId={id || ''} 
              onCancel={() => setShowTierForm(false)} 
              onSuccess={() => {
                setShowTierForm(false);
                fetchSubscriptionData();
              }}
            />
          ) : (
            <SubscriptionTierList 
              organizationId={id || ''} 
              onRefreshData={fetchSubscriptionData} 
            />
          )}
        </TabPanel>
        
        <TabPanel value={currentTab} index={1}>
          <SubscribersList organizationId={id || ''} />
        </TabPanel>
        
        <TabPanel value={currentTab} index={2}>
          <AnalyticsTab organizationId={id || ''} />
        </TabPanel>
        
        <TabPanel value={currentTab} index={3}>
          <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
            Payment History
          </Typography>
          <Alert severity="info">
            Payment history and detailed reporting will be available after your first subscription payment is processed.
          </Alert>
        </TabPanel>
        
        <TabPanel value={currentTab} index={4}>
          <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
            Subscription Settings
          </Typography>
          
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1">
                  Platform Fee: {subscriptionSettings.platformFee}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This fee is calculated on all subscription revenue and helps cover payment processing costs.
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1">
                  Next Billing Date: {subscriptionSettings.nextBillingDate?.toDate().toLocaleDateString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your organization will be charged $100 for the subscription feature on this date.
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <FormControlLabel
                  control={<Switch color="primary" checked={subscriptionSettings.enabled} />}
                  label="Subscription Feature Active"
                  disabled
                />
              </Grid>
            </Grid>
          </Paper>
        </TabPanel>
        
        <TabPanel value={currentTab} index={5}>
          <PublicMembershipLink organizationId={id || ''} />
        </TabPanel>
      </>
    );
  };
  
  // If personal account or not admin, show error
  if (!loading && (isPersonalAccount || !isOrgAdmin)) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          {error || "You don't have permission to access subscription management"}
        </Alert>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Subscription Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and manage subscription tiers for your organization members
          </Typography>
        </Box>
      </Box>
      
      {/* Show loading, error, or content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        renderContent()
      )}
    </Container>
  );
};

// Helper component for tab panels
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`subscription-tabpanel-${index}`}
      aria-labelledby={`subscription-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default OrganizationSubscriptions;
